import { ExternalDisplayObjectClipSource } from "./ExternalDisplayObjectClipSource";

import { MovieClipHelper } from "../../../graphics/utils/MovieClipHelper";
import { error } from "../../../logging/error";
import { GoodgameBitmapEngine } from "../../GoodgameBitmapEngine";
import { FlattenedImage } from "../FlattenedImage";
import { FrameStrip } from "../FrameStrip";
import { ClipColor } from "../../components/ClipColor";

import BitmapData = createjs.BitmapData;
import Bitmap = createjs.Bitmap;
import DisplayObject = createjs.DisplayObject;
import Event = createjs.Event;
import Point = createjs.Point;
import Rectangle = createjs.Rectangle;
import { TextField } from "@goodgamestudios/createjs-utilities";
import {getDefinitionByName, getQualifiedClassName} from "@goodgamestudios/createjs-utilities";
import {LoadingItem} from "../../../loader/LoadingItem";
import { ClipSourceOptimizer } from "../ClipSourceOptimizer";
import { BulkLoader } from "../../../loader/BulkLoader";


/**
 *    ExternalTexturePackerClipSource
 *
 *  loades a .swf
 *  extract all .json by sourceClassName plus "Layer_" plus z-index
 *  extract all .png by sourceClassName plus "Layer_" plus z-index of jsonDescription Textfield
 *  merge all png
 *
 *
 *     @author Alexander
 */
export class ExternalTexturePackerClipSource extends ExternalDisplayObjectClipSource {
    protected _sourceSizeW: number;
    protected _sourceSizeH: number;

    constructor() {
        super();
    }

    /**
     * init this source. loads an external spritesheet and converts it when it finished loading or already loaded
     *
     * @param spritesheetSourcePath
     * @param className of the DisplayObject
     * @param color used for debug informations
     * @param priority An int that controls which items are loaded first. Items with a higher PRIORITY will load first. If more than one item has the same PRIORITY number, the order in which they are added will be taken into consideration. Checked when adding a new item to load.
     */
    public initExternalClipSource(clipClassName: string, swfSourcePath: string, colorData: ClipColor[] = null, debugColor: number = 0, hasSubLayer: boolean = false, priority: number = 1): void {
        this._sourcePath = swfSourcePath;
        this.sourceClassName = clipClassName;
        this._colorData = colorData;
        this.debugColor = debugColor;
        this.hasSubLayer = hasSubLayer;
        this._isLoaded = false;

        let loaderObject: createjs.AbstractLoader = GoodgameBitmapEngine.bulkLoader.add(`${this._sourcePath}.json`, { id: clipClassName, type:createjs.AbstractLoader.SPRITESHEET, priority: priority });

        if (loaderObject) {
            if (loaderObject.loaded) {
                let performanceTimeOut = setTimeout(() => {
                    clearTimeout(performanceTimeOut);
                    this.onLoaded(loaderObject);
                }, (loaderObject.getResult() as any)._frames.length * 100);
            } else {
                loaderObject.addEventListener(Event.COMPLETE, this.bindFunction( this.onLoaded));
            }
        }
    }

    /**
     * converts the DisplayObject when it finished loading or already loaded
     *
     */
    protected onLoaded(event: Event | createjs.AbstractLoader = null): void {
        let spriteSheet, rawResultData;
        if (event instanceof Event) {
            event.target.removeEventListener(Event.COMPLETE, this.bindFunction(this.onLoaded));
            BulkLoader.loadedList.set(event.currentTarget.getItem().src, event.currentTarget);
            spriteSheet = event.result;
            rawResultData = JSON.parse(event.rawResult);
            event.currentTarget._rawResult = rawResultData;
        } else {
            spriteSheet = event.getResult();
            rawResultData = event.getResult(true);
            if ( typeof rawResultData === "string" ) {
                rawResultData = JSON.parse( rawResultData );
                (event as createjs.AbstractLoader as any)._rawResult = rawResultData;
            }
        }
        this.animData = [];
        //parse sheetData
        this.populateFrameArray(this.animData, spriteSheet._frames, rawResultData.sourceSize, rawResultData.animations);

        this._isLoaded = true;

        if (this.scaleFactor === 1) {
            let animationImage: HTMLImageElement = spriteSheet._images[0];
            for (let frameIndex: number = 0; frameIndex < this.animData.length; frameIndex++) {
                let frameObject: any = this.animData[frameIndex];
                let bmp = new Bitmap(animationImage);
                bmp.sourceRect = frameObject.rect;
                bmp.x = frameObject.offX;
                bmp.y = frameObject.offY;
                this._framesStrip.addFrame(bmp);
            }
        } else {
            let srcBitmapData;
            if (this.animData.length > 1) {
                srcBitmapData = new createjs.BitmapData(rawResultData.size.w, rawResultData.size.h, true); // provide the full sized spritesheet for animations
                srcBitmapData.draw(spriteSheet._images[0]); // this is the whole spritesheet
            } else {
                let data = spriteSheet.getFrame(spriteSheet.getAnimation(spriteSheet.animations[0]).frames[0]);
                srcBitmapData = new createjs.BitmapData(data.rect.width, data.rect.height, true);
                srcBitmapData.draw(data.image);
            }

            this._framesStrip = this.convertTexturePackerContainer(srcBitmapData);
        }

        this._clipLoadedAndConverted.dispatch(this);
        this._clipLoadedAndConverted.removeAll();

    }

    /**
     * parse  sheetData
     * @param arDest
     * @param src
     *
     */
    protected populateFrameArray(arDest: any[], src: any, sourceSize: any, rawAnimationFromJson: any): void {
        let entry: any;
        let item: any;
        let keyName: string;
        let srcArr: any[] = src;

        for (keyName in src) {
            entry = src[keyName];
            if (entry.rect) {
                if (sourceSize) {
                    this._sourceSizeW = sourceSize.w;
                    this._sourceSizeH = sourceSize.h;
                }
                item = entry;
                let animName: string = this.getAnimationNameByFrame( parseInt(keyName), rawAnimationFromJson );
                var matches = animName.match(/\d+/g);
                if ( matches && matches.length > 0 ) {
                    item.sortID = parseInt(matches.join(""));
                } else {
                    item.sortID = 0;
                }
                item.animName = animName;
                item.offX = 0;
                item.offY = 0;
                item.offX = Math.abs(entry.regX);
                item.offY = Math.abs(entry.regY);
                arDest.push(item);
            }
        }
        arDest.sort(this.sortOnSortID);
    }

    private getAnimationNameByFrame( frame: number, rawAnimationFromJson: any ) : string {
        for ( var keyName in rawAnimationFromJson ) {
            if ( rawAnimationFromJson[keyName].frames[0] == frame )
                return keyName;
        }
        return "-";
    }

    protected sortOnSortID(firstObject: any, secondObject: any): number {
        if (firstObject.sortID > secondObject.sortID) {
            return 1;
        } else if (firstObject.sortID < secondObject.sortID) {
            return -1;
        } else if ( firstObject.animName > secondObject.animName ) {
            return 1;
        } else if ( firstObject.animName > secondObject.animName ) {
            return -1;
        }
        return 0;
    }

    public convertClass(clipClass: Object, colorData: ClipColor[] = null, debugColor: number = 0, hasSubLayer: boolean = false): void {
        this._colorData = colorData;
        this.debugColor = debugColor;
        this.hasSubLayer = hasSubLayer;

        this._cacheId = getQualifiedClassName(this.sourceClass);
        let sourceBmp: BitmapData = new this.sourceClass();
        this._framesStrip = this.convertTexturePackerContainer(sourceBmp);
    }

    /**
     * Converts a MovieClip to a BitmapSource
     *
     * @param SourceClassInstance
     *
     */
    protected convertTexturePackerContainer(sourceBmp: BitmapData): FrameStrip {
        let flattenedImages: FlattenedImage[] = [];
        for (let frameIndex: number = 0; frameIndex < this.animData.length; frameIndex++) {
            let frameObject: any = this.animData[frameIndex];
            flattenedImages.push(this.renderSingleTile(sourceBmp, frameObject));
        }
        ClipSourceOptimizer.flattenedImagesToBitmaps(flattenedImages).forEach( bitmap => {
            this._framesStrip.addFrame(bitmap);
        });
        return this._framesStrip;
    }

    /**
     * Renders the content of the current frame of a DisplayObject to FlattenedImage
     *
     * @param SourceClassInstance
     *
     */
    protected renderSingleTile(sourceBmp: BitmapData, frameObject: any): FlattenedImage {
        let rcFrame: Rectangle;
        let regPt: Point;
        rcFrame = new Rectangle();
        rcFrame.x = frameObject.rect.x;
        rcFrame.y = frameObject.rect.y;
        rcFrame.width = frameObject.rect.width;
        rcFrame.height = frameObject.rect.height;
        regPt = new Point();
        regPt.x = frameObject.offX;
        regPt.y = frameObject.offY;
        let mFrameRect: Rectangle = new Rectangle();
        mFrameRect.width = Math.max(mFrameRect.width, rcFrame.width + regPt.x);
        mFrameRect.height = Math.max(mFrameRect.height, rcFrame.height + regPt.y);
        this.addToClipBounds(mFrameRect);
        let flatImage: FlattenedImage;
        flatImage = new FlattenedImage(rcFrame.width * this.scaleFactor, rcFrame.height * this.scaleFactor, 0 - (Math.floor(regPt.x * this.scaleFactor)), 0 - (Math.floor(regPt.y * this.scaleFactor)), this.debugColor);
        //flatImage.copyPixels( sourceBmp, rcFrame, regPt );
        flatImage.copyPixels(sourceBmp, rcFrame, new Point());
        delete frameObject.sortID;
        return flatImage;
    }

    public get sourceSizeW(): number {
        return this._sourceSizeW;
    }

    public get sourceSizeH(): number {
        return this._sourceSizeH;
    }
}
